// Canonical status/severity palette — the single source of truth for what
// "critical"/"warning"/"good" etc. look like everywhere (badges, gauges,
// progress bars, timeline icons, chart bars). Replaces the five independent
// color-mapping objects that used to live in StatusBadge/GaugeCard/RiskCard/
// ProgressWidget/TimelineCard. Hex values match src/data/chartTheme.js's
// CHART_STATUS so a "critical" reads identically in a table badge and a chart.
//
// Buckets are the fixed vocabulary every component maps its own domain
// strings onto locally (e.g. a page's "Active"/"Pending"/"Resolved" ->
// STATUS_KEY.good/.neutral/.good) — this file only owns bucket -> visual,
// not the (sometimes conflicting) meaning of a raw label across pages.
export const STATUS_KEY = {
  good: "good",
  info: "info",
  warning: "warning",
  serious: "serious",
  critical: "critical",
  neutral: "neutral",
};

export const STATUS = {
  good:     { hex: "#0ca30c", bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-200", bar: "bg-emerald-500" },
  info:     { hex: "#2a78d6", bg: "bg-blue-100",    text: "text-blue-700",    border: "border-blue-200",    bar: "bg-blue-500"    },
  warning:  { hex: "#fab219", bg: "bg-amber-100",   text: "text-amber-700",   border: "border-amber-200",   bar: "bg-amber-500"   },
  serious:  { hex: "#ec835a", bg: "bg-orange-100",  text: "text-orange-700",  border: "border-orange-200",  bar: "bg-orange-500"  },
  critical: { hex: "#d03b3b", bg: "bg-red-100",     text: "text-red-700",    border: "border-red-200",     bar: "bg-red-500"     },
  neutral:  { hex: "#64748b", bg: "bg-slate-100",   text: "text-slate-600",  border: "border-slate-200",   bar: "bg-slate-400"   },
};

export const normalizeKey = (s) => (s ?? "").toString().trim().toLowerCase();

// Full Tailwind badge classes, e.g. for StatusBadge.
export const statusClasses = (bucket) => {
  const s = STATUS[bucket] || STATUS.neutral;
  return `${s.bg} ${s.text} border ${s.border}`;
};

// Raw hex, e.g. for gauge fills / chart bars / icon colors.
export const statusHex = (bucket) => (STATUS[bucket] || STATUS.neutral).hex;

// Generic numeric-threshold -> bucket resolver. `bands` is a list of
// {min, bucket} sorted descending by `min`; the first band whose `min` the
// value clears wins, falling back to the last band. Pass higherIsBetter:false
// for load/stress-type metrics (higher = worse) vs the default score/
// performance metrics (higher = better) — callers keep their own domain-
// specific thresholds (e.g. GaugeCard's 85/70/50 vs ProgressWidget's
// 90/75/55), only the resulting color comes from the shared palette.
export const thresholdStatus = (value, bands) => {
  for (const b of bands) {
    if (value >= b.min) return b.bucket;
  }
  return bands[bands.length - 1]?.bucket || STATUS_KEY.neutral;
};
